import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";
import { HubConnection, INITIAL_HUB_STATE } from "./hubConnection";
import { UNSUPPORTED_FIRMWARE, describeFirmware } from "./firmware";
import { compileWithRetry, unreachable, unreachableHelp } from "./compile";
import { IDENTIFY_PROGRAM } from "@/rc/prelude";
import { API, errorMessage } from "@/lib/api";

const HubContext = createContext(null);
const MAX_LOGS = 400;

const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export function HubProvider({ children }) {
  const [hub, setHub] = useState(INITIAL_HUB_STATE);
  const [logs, setLogs] = useState([]);
  const partial = useRef("");
  const connRef = useRef(null);

  const addLog = useCallback((kind, text) => {
    setLogs((l) => [...l, { id: `${Date.now()}-${Math.random()}`, t: new Date(), kind, text }].slice(-MAX_LOGS));
  }, []);

  const onStdout = useCallback((chunk) => {
    const lines = (partial.current + chunk).split(/\r?\n/);
    partial.current = lines.pop();
    lines.forEach((line) => addLog("out", line));
  }, [addLog]);

  if (!connRef.current) {
    connRef.current = new HubConnection({ onUpdate: setHub, onStdout, onLog: addLog });
  }

  const value = useMemo(() => {
    const conn = connRef.current;
    const sendPython = async (code, api, label, countRun = false) => {
      addLog("info", `Preparing ${label.toLowerCase()}…`);
      let compiled;
      try {
        compiled = await compileWithRetry(api, code, (n, total) => addLog("info", `Couldn't reach HubPilot, trying again (${n} of ${total})…`));
      } catch (e) {
        throw new Error(unreachable(e) ? unreachableHelp() : `Could not prepare the robot setup: ${errorMessage(e)}`);
      }
      addLog("info", `Sending ${compiled.size} bytes to ${conn.state.name}…`);
      await conn.downloadAndRun(b64ToBytes(compiled.mpy));
      addLog("info", `${label} is running on ${conn.state.name}.`);
      if (countRun && compiled.run_id) axios.post(`${API}/runs/${compiled.run_id}/started`).catch(() => {});
    };
    return {
      ...hub,
      logs,
      supported: typeof navigator !== "undefined" && Boolean(navigator.bluetooth),
      clearLogs: () => setLogs([]),
      addLog,
      connect: async () => {
        try {
          await conn.connect();
          addLog("info", `Connected to ${conn.state.name}.`);
          return true;
        } catch (e) {
          if (e?.name === "NotFoundError") return false;
          throw e;
        }
      },
      disconnect: () => conn.disconnect(),
      stop: async () => {
        await conn.stop();
        addLog("info", "Stop sent to hub.");
      },
      runPython: async (code, api) => {
        if (describeFirmware(conn.state.firmware).unsupported) throw new Error(UNSUPPORTED_FIRMWARE);
        return sendPython(code, api, "Your robot setup", true);
      },
      identify: (api) => sendPython(IDENTIFY_PROGRAM, api, "The identify flash"),
    };
  }, [hub, logs, addLog]);

  return <HubContext.Provider value={value}>{children}</HubContext.Provider>;
}

export const useHub = () => useContext(HubContext);
